import { ProjectsSelect } from "@selector/shared/components";
import { type FC, useEffect, useMemo, useState } from "react";
import { Item } from "react-stately";
import { useDebouncedCallback } from "use-debounce";
import { Card, CardSection, MenuOptionItem, SelectList, TextField } from "widgets";
import type { FileDefinitionType } from "widgets/utils";

import {
  type ContextualFileFilter,
  type ContextualFileFilterOption,
  useFileSearchStore,
} from "@/modules/files/stores/file-search";
import { computeContextualFilterIds } from "@/modules/files/utils/file.ts";
import { getContextualFileFilterOptions } from "@/modules/files/utils/getContextualFileFilterOptions";

export interface FileSearchFiltersProps {
  fileDefinitionTypes?: FileDefinitionType[] | string[] | null;
}

const FileSearchFilters: FC<FileSearchFiltersProps> = (props) => {
  const suggestionsContext = useFileSearchStore((state) => state.suggestionsContext);
  const sortExpression = useFileSearchStore((state) => state.sortExpression);
  const activeContextualFilter = useFileSearchStore((state) => state.activeContextualFilter);
  const setActiveContextualFilter = useFileSearchStore((state) => state.setActiveContextualFilter);
  const searchProjectFiles = useFileSearchStore((state) => state.searchProjectFiles);

  const [projectId, setProjectId] = useState<number>();
  const [textSearchValue, setTextSearchValue] = useState<string>("");
  const debouncedSetTextSearchValue = useDebouncedCallback((searchTerms: string) => {
    setTextSearchValue(searchTerms.trim());
  }, 500);

  const contextualOptions: ContextualFileFilterOption[] = useMemo((): ContextualFileFilterOption[] => {
    return getContextualFileFilterOptions(suggestionsContext);
  }, [suggestionsContext]);

  // whenever a filter changes we trigger the fetching of new results
  useEffect(() => {
    const { genomeIds, eventIds, sampleIds, parentIds } = computeContextualFilterIds(
      suggestionsContext,
      activeContextualFilter,
    );
    void searchProjectFiles(
      textSearchValue,
      props.fileDefinitionTypes,
      projectId,
      genomeIds,
      eventIds,
      sampleIds,
      parentIds,
      sortExpression,
    );
  }, [projectId, textSearchValue, props.fileDefinitionTypes, sortExpression, activeContextualFilter]);

  return (
    <Card noBorder scrollable>
      {contextualOptions.filter((option) => option.active).length ? (
        <CardSection title="Suggestions">
          <SelectList
            aria-label="Suggestions"
            items={contextualOptions.filter((option) => option.active)}
            selectionMode={"single"}
            selectedKeys={activeContextualFilter ? [activeContextualFilter] : undefined}
            onSelectionChange={(keys) => {
              if (keys instanceof Set) {
                const firstKey: ContextualFileFilter | undefined = keys.values().next().value;
                setActiveContextualFilter(firstKey);
              }
            }}
            style={{ padding: 0 }}
            noBorder
          >
            {(item) => (
              <Item key={item.key}>
                <MenuOptionItem title={item.label} description={item.description} useHover />
              </Item>
            )}
          </SelectList>
        </CardSection>
      ) : null}

      <CardSection title="Filter By">
        <TextField
          label="File or sample or organism name"
          placeholder="Enter text"
          onChange={(value) => debouncedSetTextSearchValue(value)}
        />
        <ProjectsSelect
          label="Project"
          onSelectionChange={(project) => {
            if (project?.id === -1) {
              // => All Projects
              setProjectId(undefined);
            } else {
              setProjectId(project?.id);
            }
          }}
          includeAllProjects
          includeStagedProjects
          defaultSelection="all"
          initialSelectedProjectId={projectId}
        />
      </CardSection>
    </Card>
  );
};

export { FileSearchFilters };
